import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'react-toastify';

const TestsManagement = () => {
  const { adminToken } = useAdminAuth();
  const queryClient = useQueryClient();
  const [showAddTestModal, setShowAddTestModal] = useState(false);
  const [showAddQuestionsModal, setShowAddQuestionsModal] = useState(false);
  const [showLevelSelector, setShowLevelSelector] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [notification, setNotification] = useState(null);

   const fetchTests = async () => {
    try {
      const response = await fetch('https://question-manager-backend.onrender.com/api/admin/tests/admin', {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('فشل في جلب الاختبارات');
      }
      
      return response.json();
    } catch (error) {
      showNotification('error', 'فشل في جلب الاختبارات: ' + error.message);
      throw error;
    }
  };

   const fetchClasses = async () => {
    try {
      const response = await fetch('https://question-manager-backend.onrender.com/api/classes/admin', {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('فشل في جلب الفصول');
      }
      
      return response.json();
    } catch (error) {
      showNotification('error', 'فشل في جلب الفصول: ' + error.message);
      throw error;
    }
  };

   const { data: testsData, isLoading: testsLoading, error: testsError } = useQuery({
    queryKey: ['adminTests'],
    queryFn: fetchTests,
  });

  const { data: classesData, isLoading: classesLoading } = useQuery({
    queryKey: ['adminClasses'],
    queryFn: fetchClasses,
  });

   const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

   const addTestMutation = useMutation({
    mutationFn: async (newTest) => {
      try {
        const response = await fetch('https://question-manager-backend.onrender.com/api/admin/tests', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newTest),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في إضافة الاختبار');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في إضافة الاختبار: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم إضافة الاختبار بنجاح!');
      queryClient.invalidateQueries(['adminTests']);
      setShowAddTestModal(false);
    },
  });

   const addQuestionsMutation = useMutation({
    mutationFn: async ({ testId, levelNumber, formData }) => {
      try {
        console.log('إرسال الأسئلة:', {
          testId,
          levelNumber,
          formData: Array.from(formData.entries())
        });

        const response = await fetch(`https://question-manager-backend.onrender.com/api/admin/tests/${testId}/levels/${levelNumber}/questions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
          },
          body: formData,
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في إضافة الأسئلة');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في إضافة الأسئلة: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم إضافة الأسئلة بنجاح!');
      queryClient.invalidateQueries(['adminTests']);
      setShowAddQuestionsModal(false);
      setShowLevelSelector(false);
      setSelectedTest(null);
      setSelectedLevel(null);
    },
    onError: (error) => {
      showNotification('error', error.message);
    }
  });

   const editQuestionMutation = useMutation({
    mutationFn: async ({ questionId, formData }) => {
      try {
        const response = await fetch(`https://question-manager-backend.onrender.com/api/admin/tests/questions/${questionId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
          },
          body: formData,
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في تعديل السؤال');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في تعديل السؤال: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم تعديل السؤال بنجاح!');
      queryClient.invalidateQueries(['adminTests']);
      setShowEditQuestionModal(false);
      setSelectedQuestion(null);
    },
    onError: (error) => {
      showNotification('error', error.message);
    }
  });

   const deleteTestMutation = useMutation({
    mutationFn: async (testId) => {
      try {
        const response = await fetch(`https://question-manager-backend.onrender.com/api/admin/tests/${testId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في حذف الاختبار');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في حذف الاختبار: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم حذف الاختبار بنجاح');
      queryClient.invalidateQueries(['adminTests']);
    },
  });

   const updateTestSettingsMutation = useMutation({
    mutationFn: async ({ testId, settings }) => {
      try {
        const response = await fetch(`https://question-manager-backend.onrender.com/api/admin/tests/${testId}/settings`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(settings),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في تحديث الإعدادات');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في تحديث الإعدادات: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم تحديث الإعدادات بنجاح');
      queryClient.invalidateQueries(['adminTests']);
      setShowSettingsModal(false);
      setSelectedTest(null);
    },
  });

   const Notification = ({ notification }) => {
    if (!notification) return null;
    
    const bgColor = notification.type === 'success' ? 'bg-green-500' : 'bg-red-500';
    
    return (
      <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg text-white shadow-lg z-50 ${bgColor} animate-fade-in-out`}>
        <div className="flex items-center">
          {notification.type === 'success' ? (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          ) : (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          )}
          <span>{notification.message}</span>
        </div>
      </div>
    );
  };

   const LevelSelector = ({ test, onLevelSelect, onClose }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                اختر المستوى - {test?.title}
              </h2>
              <button 
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-4">
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <h3 className="text-lg font-semibold mb-3">اختر المستوى</h3>
              <div className="grid grid-cols-3 gap-2">
                {test.levels.map((level) => (
                  <button
                    key={level.id}
                    onClick={() => onLevelSelect(level.levelNumber)}
                    className={`px-3 py-2 rounded-lg text-center transition-colors ${
                      level.questions.length >= level.numberOfQuestions
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                    }`}
                  >
                    <div className="font-medium">المستوى {level.levelNumber}</div>
                    <div className="text-xs mt-1">
                      {level.questions.length}/{level.numberOfQuestions}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

   const QuestionsViewer = ({ test, level, onEditQuestion, onClose }) => {
    const selectedLevelData = test.levels.find(l => l.levelNumber === level);
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                أسئلة {test?.title} - المستوى {level}
              </h2>
              <button 
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <span className="text-gray-700">عدد الأسئلة: </span>
                <span className="font-medium">
                  {selectedLevelData?.questions.length || 0}/{selectedLevelData?.numberOfQuestions || 0}
                </span>
              </div>
              <button
                onClick={() => {
                  setSelectedLevel(level);
                  setShowAddQuestionsModal(true);
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                إضافة أسئلة جديدة
              </button>
            </div>
            
            {!selectedLevelData?.questions.length ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
                <p className="text-gray-600 mb-3">لا توجد أسئلة في هذا المستوى بعد</p>
                <button
                  onClick={() => {
                    setSelectedLevel(level);
                    setShowAddQuestionsModal(true);
                  }}
                  className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 px-4 py-2 rounded-lg text-sm transition-colors inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  أضف أول سؤال
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedLevelData.questions.map((question, index) => (
                  <div key={question.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-indigo-100 text-indigo-800 rounded-full flex items-center justify-center font-medium mr-3 mt-1">
                          {index + 1}
                        </div>
                        <div>
                          <div className="text-gray-800 font-medium mb-1">{question.questionText}</div>
                          <div className="text-xs text-gray-500 mb-2 flex items-center space-x-2 space-x-reverse">
                            <span className={`px-2 py-1 rounded-full ${
                              question.questionType === 'text-only' ? 'bg-blue-100 text-blue-800' :
                              question.questionType === 'image-question' ? 'bg-purple-100 text-purple-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {question.questionType === 'text-only' ? 'نص فقط' :
                               question.questionType === 'image-question' ? 'سؤال بصورة' :
                               'خيارات بصورة'}
                            </span>
                            <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                              {question.points || 1} نقطة
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => onEditQuestion(question)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="p-4 border-t border-gray-200 bg-white sticky bottom-0">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

   const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
      <div className="flex space-x-2">
        <div className="h-8 bg-gray-200 rounded w-20"></div>
        <div className="h-8 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
  );

   const AddTestForm = () => {
    const [formData, setFormData] = useState({
      title: '',
      description: '',
      classId: '',
      heartsPerAttempt: 3,
      hintsPerAttempt: 2,
      isPublic: true,
      levels: [{ levelNumber: 1, numberOfQuestions: 1 }]
    });

    const addLevel = () => {
      setFormData(prev => ({
        ...prev,
        levels: [...prev.levels, { 
          levelNumber: prev.levels.length + 1, 
          numberOfQuestions: 1 
        }]
      }));
    };

    const removeLevel = (index) => {
      if (formData.levels.length > 1) {
        setFormData(prev => ({
          ...prev,
          levels: prev.levels.filter((_, i) => i !== index)
            .map((level, idx) => ({ ...level, levelNumber: idx + 1 }))
        }));
      }
    };

    const updateLevel = (index, field, value) => {
      setFormData(prev => ({
        ...prev,
        levels: prev.levels.map((level, i) => 
          i === index ? { ...level, [field]: value } : level
        )
      }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      addTestMutation.mutate(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">إضافة اختبار جديد</h2>
              <button 
                onClick={() => setShowAddTestModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4">
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">عنوان الاختبار</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الوصف</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الفصل</label>
                <select
                  required
                  value={formData.classId}
                  onChange={(e) => setFormData(prev => ({ ...prev, classId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">اختر الفصل</option>
                  {classesData?.classes.map((classItem) => (
                    <option key={classItem.id} value={classItem.id}>
                      {classItem.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">عدد القلوب</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.heartsPerAttempt}
                    onChange={(e) => setFormData(prev => ({ ...prev, heartsPerAttempt: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">عدد التلميحات</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.hintsPerAttempt}
                    onChange={(e) => setFormData(prev => ({ ...prev, hintsPerAttempt: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">المستويات</label>
                  <button
                    type="button"
                    onClick={addLevel}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1 rounded-lg text-sm flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    إضافة مستوى
                  </button>
                </div>
                
                <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
                  {formData.levels.map((level, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-100">
                      <div className="w-8 h-8 bg-indigo-100 text-indigo-800 font-bold rounded-full flex items-center justify-center">
                        {level.levelNumber}
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-gray-600 mb-1">عدد الأسئلة</label>
                        <input
                          type="number"
                          min="1"
                          value={level.numberOfQuestions}
                          onChange={(e) => updateLevel(index, 'numberOfQuestions', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      {formData.levels.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLevel(index)}
                          className="bg-red-100 text-red-700 p-2 rounded-lg hover:bg-red-200"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center">
                <input
                  id="isPublic"
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="isPublic" className="mr-2 text-sm text-gray-700">
                  اختبار عام (متاح للجميع)
                </label>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end border-t pt-4 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={() => setShowAddTestModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={addTestMutation.isLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {addTestMutation.isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري الإضافة...
                  </>
                ) : 'إضافة الاختبار'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

   const AddQuestionsForm = () => {
    const [questions, setQuestions] = useState([
      {
        questionText: '',
        options: ['', '', '', ''],
        correctAnswer: '',
        questionType: 'text-only'
      }
    ]);

    const addQuestion = () => {
      setQuestions(prev => [...prev, {
        questionText: '',
        options: ['', '', '', ''],
        correctAnswer: '',
        questionType: 'text-only'
      }]);
    };

    const removeQuestion = (index) => {
      if (questions.length > 1) {
        setQuestions(prev => prev.filter((_, i) => i !== index));
      }
    };

    const updateQuestion = (index, field, value) => {
      setQuestions(prev => prev.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      ));
    };

    const updateOption = (qIndex, oIndex, value) => {
      setQuestions(prev => prev.map((q, i) => 
        i === qIndex ? {
          ...q,
          options: q.options.map((opt, j) => j === oIndex ? value : opt)
        } : q
      ));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
       const isValid = questions.every((question, index) => {
        if (!question.questionText.trim()) {
          showNotification('error', `يرجى إدخال نص السؤال ${index + 1}`);
          return false;
        }
        if (question.options.some(opt => !opt.trim())) {
          showNotification('error', `يرجى إدخال جميع الخيارات للسؤال ${index + 1}`);
          return false;
        }
        if (!question.correctAnswer.trim()) {
          showNotification('error', `يرجى اختيار الإجابة الصحيحة للسؤال ${index + 1}`);
          return false;
        }
        return true;
      });

      if (!isValid) return;

      const formData = new FormData();
      
       formData.append('questions', JSON.stringify(questions.map(q => ({
        questionText: q.questionText,
        options: q.options,
        correctAnswer: q.correctAnswer,
        questionType: q.questionType
      }))));

       questions.forEach((question, qIndex) => {
         const questionImageInput = document.getElementById(`questionImage_${qIndex}`);
        if (questionImageInput && questionImageInput.files[0]) {
          formData.append(`questionImage_${qIndex}`, questionImageInput.files[0]);
        }

         if (question.questionType === 'image-options') {
          question.options.forEach((_, oIndex) => {
            const optionImageInput = document.getElementById(`optionImage_${qIndex}_${oIndex}`);
            if (optionImageInput && optionImageInput.files[0]) {
              formData.append(`optionImages_${qIndex}`, optionImageInput.files[0]);
            }
          });
        }
      });

      console.log('إرسال البيانات:', {
        testId: selectedTest.id,
        levelNumber: selectedLevel,
        questionsCount: questions.length
      });

      addQuestionsMutation.mutate({
        testId: selectedTest.id,
        levelNumber: selectedLevel,
        formData
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                إضافة أسئلة - {selectedTest?.title} - المستوى {selectedLevel}
              </h2>
              <button 
                onClick={() => {
                  setShowAddQuestionsModal(false);
                  setSelectedLevel(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4">
            {questions.map((question, qIndex) => (
              <div key={qIndex} className="mb-6 border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-indigo-100 text-indigo-800 font-bold rounded-full flex items-center justify-center mr-2">
                      {qIndex + 1}
                    </div>
                    <h3 className="text-lg font-semibold">السؤال</h3>
                  </div>
                  
                  {questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQuestion(qIndex)}
                      className="bg-red-100 text-red-700 p-2 rounded-lg hover:bg-red-200"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">نص السؤال</label>
                      <input
                        type="text"
                        required
                        value={question.questionText}
                        onChange={(e) => updateQuestion(qIndex, 'questionText', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="أدخل نص السؤال..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">نوع السؤال</label>
                      <select
                        value={question.questionType}
                        onChange={(e) => updateQuestion(qIndex, 'questionType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="text-only">نص فقط</option>
                        <option value="image-question">سؤال بصورة</option>
                        <option value="image-options">خيارات بصورة</option>
                      </select>
                    </div>
                  </div>
                  
                  {question.questionType === 'image-question' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">صورة السؤال</label>
                      <input
                        type="file"
                        id={`questionImage_${qIndex}`}
                        accept="image/*"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">اختر صورة للسؤال (اختياري)</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">الخيارات</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center mb-2">
                            <div className={`w-6 h-6 rounded-full mr-2 flex items-center justify-center text-xs font-bold ${
                              String.fromCharCode(65 + oIndex) === 'A' ? 'bg-red-100 text-red-700' :
                              String.fromCharCode(65 + oIndex) === 'B' ? 'bg-blue-100 text-blue-700' :
                              String.fromCharCode(65 + oIndex) === 'C' ? 'bg-green-100 text-green-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {String.fromCharCode(65 + oIndex)}
                            </div>
                            <label className="text-sm font-medium text-gray-700">الخيار {oIndex + 1}</label>
                          </div>
                          
                          <input
                            type="text"
                            required
                            value={option}
                            onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                            placeholder={`أدخل نص الخيار ${oIndex + 1}...`}
                          />
                          
                          {question.questionType === 'image-options' && (
                            <div>
                              <input
                                type="file"
                                id={`optionImage_${qIndex}_${oIndex}`}
                                accept="image/*"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">صورة الخيار (اختياري)</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الإجابة الصحيحة</label>
                    <select
                      required
                      value={question.correctAnswer}
                      onChange={(e) => updateQuestion(qIndex, 'correctAnswer', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">اختر الإجابة الصحيحة</option>
                      {question.options.map((option, oIndex) => (
                        <option key={oIndex} value={option}>
                          {option || `الخيار ${oIndex + 1} (${String.fromCharCode(65 + oIndex)})`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
            
            <div className="my-4">
              <button
                type="button"
                onClick={addQuestion}
                className="w-full py-3 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                إضافة سؤال آخر
              </button>
            </div>
            
            <div className="flex gap-2 justify-end border-t pt-4 mt-4 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={() => {
                  setShowAddQuestionsModal(false);
                  setSelectedLevel(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={addQuestionsMutation.isLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {addQuestionsMutation.isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري الإضافة...
                  </>
                ) : `إضافة ${questions.length} سؤال`}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

   const EditQuestionForm = () => {
    const [formData, setFormData] = useState({
      questionText: selectedQuestion.questionText,
      options: selectedQuestion.options || ['', '', '', ''],
      correctAnswer: selectedQuestion.correctAnswer || '',
      explanation: selectedQuestion.explanation || '',
      level: selectedQuestion.level || 1,
      points: selectedQuestion.points || 1,
      questionType: selectedQuestion.questionType || 'text-only'
    });
    
     const [questionImageFile, setQuestionImageFile] = useState(null);
    const [optionImageFiles, setOptionImageFiles] = useState([null, null, null, null]);

    const updateOption = (index, value) => {
      setFormData(prev => ({
        ...prev,
        options: prev.options.map((opt, i) => i === index ? value : opt)
      }));
    };

    const updateOptionImage = (index, file) => {
      const newFiles = [...optionImageFiles];
      newFiles[index] = file;
      setOptionImageFiles(newFiles);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
       if (!formData.questionText.trim()) {
        showNotification('error', 'يرجى إدخال نص السؤال');
        return;
      }
      
      if (formData.options.some(opt => !opt.trim())) {
        showNotification('error', 'يرجى إدخال جميع الخيارات');
        return;
      }
      
      if (!formData.correctAnswer.trim()) {
        showNotification('error', 'يرجى اختيار الإجابة الصحيحة');
        return;
      }

       const submitData = new FormData();
      
       submitData.append('questionText', formData.questionText);
      submitData.append('questionType', formData.questionType);
      formData.options.forEach((option, index) => {
        submitData.append(`options[${index}]`, option);
      });
      submitData.append('correctAnswer', formData.correctAnswer);
      submitData.append('explanation', formData.explanation);
      submitData.append('level', formData.level);
      submitData.append('points', formData.points);

       if (formData.questionType === 'image-question' && questionImageFile) {
        submitData.append('questionImage', questionImageFile);
      }
      
      if (formData.questionType === 'image-options') {
        optionImageFiles.forEach((file, index) => {
          if (file) {
            submitData.append('optionsImages', file);
          }
        });
      }

      editQuestionMutation.mutate({
        questionId: selectedQuestion.id,
        formData: submitData
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                تعديل السؤال
              </h2>
              <button 
                onClick={() => {
                  setShowEditQuestionModal(false);
                  setSelectedQuestion(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">نص السؤال</label>
                <input
                  type="text"
                  required
                  value={formData.questionText}
                  onChange={(e) => setFormData(prev => ({ ...prev, questionText: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="أدخل نص السؤال..."
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع السؤال</label>
                  <select
                    value={formData.questionType}
                    onChange={(e) => setFormData(prev => ({ ...prev, questionType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="text-only">نص فقط</option>
                    <option value="image-question">سؤال بصورة</option>
                    <option value="image-options">خيارات بصورة</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المستوى</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.level}
                    onChange={(e) => setFormData(prev => ({ ...prev, level: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">النقاط</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.points}
                    onChange={(e) => setFormData(prev => ({ ...prev, points: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              {formData.questionType === 'image-question' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">صورة السؤال</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setQuestionImageFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">اختر صورة للسؤال</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الخيارات</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {formData.options.map((option, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center mb-2">
                        <div className={`w-6 h-6 rounded-full mr-2 flex items-center justify-center text-xs font-bold ${
                          String.fromCharCode(65 + index) === 'A' ? 'bg-red-100 text-red-700' :
                          String.fromCharCode(65 + index) === 'B' ? 'bg-blue-100 text-blue-700' :
                          String.fromCharCode(65 + index) === 'C' ? 'bg-green-100 text-green-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <label className="text-sm font-medium text-gray-700">الخيار {index + 1}</label>
                      </div>
                      
                      <input
                        type="text"
                        required
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                        placeholder={`أدخل نص الخيار ${index + 1}...`}
                      />
                      
                      {formData.questionType === 'image-options' && (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => updateOptionImage(index, e.target.files[0])}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">صورة الخيار</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الإجابة الصحيحة</label>
                <select
                  required
                  value={formData.correctAnswer}
                  onChange={(e) => setFormData(prev => ({ ...prev, correctAnswer: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">اختر الإجابة الصحيحة</option>
                  {formData.options.map((option, index) => (
                    <option key={index} value={option}>
                      {option || `الخيار ${index + 1} (${String.fromCharCode(65 + index)})`}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الشرح (اختياري)</label>
                <textarea
                  value={formData.explanation}
                  onChange={(e) => setFormData(prev => ({ ...prev, explanation: e.target.value }))}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="أدخل شرحًا للإجابة (اختياري)..."
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end border-t pt-4 mt-6 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={() => {
                  setShowEditQuestionModal(false);
                  setSelectedQuestion(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={editQuestionMutation.isLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {editQuestionMutation.isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري الحفظ...
                  </>
                ) : 'حفظ التغييرات'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

   const TestSettingsForm = () => {
    const [settings, setSettings] = useState({
      heartsPerAttempt: selectedTest?.heartsPerAttempt || 3,
      hintsPerAttempt: selectedTest?.hintsPerAttempt || 2,
      isPublic: selectedTest?.isPublic || true,
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      updateTestSettingsMutation.mutate({
        testId: selectedTest.id,
        settings
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                إعدادات الاختبار - {selectedTest?.title}
              </h2>
              <button 
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4">
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">عدد القلوب لكل محاولة</label>
                <div className="flex items-center">
                  <button 
                    type="button" 
                    onClick={() => setSettings(prev => ({ ...prev, heartsPerAttempt: Math.max(1, prev.heartsPerAttempt - 1) }))}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-l-lg px-3 py-2"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={settings.heartsPerAttempt}
                    onChange={(e) => setSettings(prev => ({ ...prev, heartsPerAttempt: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border-y border-gray-300 text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button 
                    type="button"
                    onClick={() => setSettings(prev => ({ ...prev, heartsPerAttempt: prev.heartsPerAttempt + 1 }))}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-r-lg px-3 py-2"
                  >
                    +
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">عدد التلميحات لكل محاولة</label>
                <div className="flex items-center">
                  <button 
                    type="button" 
                    onClick={() => setSettings(prev => ({ ...prev, hintsPerAttempt: Math.max(0, prev.hintsPerAttempt - 1) }))}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-l-lg px-3 py-2"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={settings.hintsPerAttempt}
                    onChange={(e) => setSettings(prev => ({ ...prev, hintsPerAttempt: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border-y border-gray-300 text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button 
                    type="button"
                    onClick={() => setSettings(prev => ({ ...prev, hintsPerAttempt: prev.hintsPerAttempt + 1 }))}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-r-lg px-3 py-2"
                  >
                    +
                  </button>
                </div>
              </div>
              
              <div className="flex items-center">
                <input
                  id="isPublic"
                  type="checkbox"
                  checked={settings.isPublic}
                  onChange={(e) => setSettings(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="isPublic" className="mr-2 text-sm text-gray-700">
                  اختبار عام (متاح للجميع)
                </label>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end border-t pt-4">
              <button
                type="button"
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={updateTestSettingsMutation.isLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {updateTestSettingsMutation.isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري الحفظ...
                  </>
                ) : 'حفظ الإعدادات'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

   const DeleteConfirmation = ({ test, onCancel, onConfirm }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="text-center mb-6">
            <div className="bg-red-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">تأكيد حذف الاختبار</h2>
            <p className="text-gray-600">
              هل أنت متأكد أنك تريد حذف الاختبار "{test.title}"؟ 
              <br />
              هذا الإجراء لا يمكن التراجع عنه وسيتم حذف جميع الأسئلة المرتبطة به.
            </p>
          </div>
          
          <div className="flex gap-2 justify-center">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              نعم، احذف الاختبار
            </button>
          </div>
        </div>
      </div>
    );
  };

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);
  const [showQuestionsViewer, setShowQuestionsViewer] = useState(false);
  const [selectedViewLevel, setSelectedViewLevel] = useState(null);

  const handleDeleteTest = () => {
    if (testToDelete) {
      deleteTestMutation.mutate(testToDelete.id);
      setShowDeleteConfirmation(false);
      setTestToDelete(null);
    }
  };

   const handleOpenQuestionsModal = (test) => {
    setSelectedTest(test);
    setShowLevelSelector(true);
  };

   const handleLevelSelect = (levelNumber) => {
    setSelectedLevel(levelNumber);
    setShowLevelSelector(false);
    
     const level = selectedTest.levels.find(l => l.levelNumber === levelNumber);
    
    if (level && level.questions.length > 0) {
      setSelectedViewLevel(levelNumber);
      setShowQuestionsViewer(true);
    } else {
      setShowAddQuestionsModal(true);
    }
  };

   const handleEditQuestion = (question) => {
    setSelectedQuestion(question);
    setShowEditQuestionModal(true);
    setShowQuestionsViewer(false);
  };

  if (testsLoading || classesLoading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} className="bg-white rounded-lg shadow p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
                <div className="flex gap-2">
                  <div className="h-10 bg-gray-200 rounded flex-1"></div>
                  <div className="h-10 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (testsError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-red-100">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">حدث خطأ</h2>
          <p className="text-gray-600 mb-6">{testsError.message}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

   const { tests, count } = testsData;

  return (
    <div className="min-h-screen bg-gray-100">
       <Notification notification={notification} />
      
       <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto p-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">إدارة الاختبارات</h1>
            </div>
            <button
              onClick={() => setShowAddTestModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center self-start"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              إضافة اختبار جديد
            </button>
          </div>
        </div>
      </header>

      <div className="bg-white">
  <div className="max-w-6xl mx-auto px-4 py-6">

     <div className="grid grid-cols-3 gap-3">

       <div className="bg-white border border-purple-700 rounded-lg p-3 shadow-sm">
        <p className="text-gray-600 text-xs md:text-sm mb-1">إجمالي الاختبارات</p>
        <div className="flex items-end">
          <span className="text-xl md:text-3xl font-bold">{count}</span>
          <span className="text-xs ml-2 mb-1">اختبار</span>
        </div>
      </div>

       <div className="bg-white border border-purple-700 rounded-lg p-3 shadow-sm">
        <p className="text-gray-600 text-xs md:text-sm mb-1">الاختبارات العامة</p>
        <div className="flex items-end">
          <span className="text-xl md:text-3xl font-bold">{tests.filter(t => t.isPublic).length}</span>
          <span className="text-xs ml-2 mb-1">اختبار</span>
        </div>
      </div>

       <div className="bg-white border border-purple-700 rounded-lg p-3 shadow-sm">
        <p className="text-gray-600 text-xs md:text-sm mb-1">الاختبارات الخاصة</p>
        <div className="flex items-end">
          <span className="text-xl md:text-3xl font-bold">{tests.filter(t => !t.isPublic).length}</span>
          <span className="text-xs ml-2 mb-1">اختبار</span>
        </div>
      </div>

    </div>
  </div>
</div>

       <div className="max-w-6xl mx-auto px-4 py-6">
        {tests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-indigo-100">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">لا توجد اختبارات حالياً</h3>
            <p className="text-gray-600 mb-6">قم بإضافة اختبار جديد للبدء</p>
            <button
              onClick={() => setShowAddTestModal(true)}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              إضافة اختبار جديد
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tests.map((test) => (
              <div key={test.id} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 transition-all hover:shadow-md">
                <div className={`h-2 ${test.isPublic ? 'bg-green-500' : 'bg-amber-500'}`}></div>
                <div className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-gray-800">{test.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      test.isPublic ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {test.isPublic ? 'عام' : 'خاص'}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{test.description || 'لا يوجد وصف'}</p>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-4">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                      <span>الفصل: {test.class?.name || 'غير محدد'}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                      </svg>
                      <span>المستويات: {test.totalLevels}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-red-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                      </svg>
                      <span>القلوب: {test.heartsPerAttempt}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-yellow-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                      </svg>
                      <span>التلميحات: {test.hintsPerAttempt}</span>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-700 text-xs mb-2">تقدم المستويات:</h4>
                    <div className="mb-4">
                      {test.levels.map((level) => {
                        const progress = Math.min(100, Math.round((level.actualQuestionsCount / level.numberOfQuestions) * 100));
                        return (
                          <div key={level.id} className="mb-2">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>المستوى {level.levelNumber}</span>
                              <span className={`font-medium ${
                                progress >= 100 ? 'text-green-600' : 'text-amber-600'
                              }`}>
                                {level.actualQuestionsCount}/{level.numberOfQuestions} ({progress}%)
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div 
                                className={`h-2.5 rounded-full ${
                                  progress >= 100 ? 'bg-green-500' : 'bg-amber-500'
                                }`}
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setSelectedTest(test);
                        setShowSettingsModal(true);
                      }}
                      className="flex-1 text-gray-700 bg-gray-100 hover:bg-gray-200 px-2 py-2 rounded-md text-sm transition-colors flex items-center justify-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                      الإعدادات
                    </button>
                    <div className="relative group">
                      <button
                        onClick={() => handleOpenQuestionsModal(test)}
                        className="flex-1 text-white bg-indigo-600 hover:bg-indigo-700 px-2 py-2 rounded-md text-sm transition-colors flex items-center justify-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        الأسئلة
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setTestToDelete(test);
                        setShowDeleteConfirmation(true);
                      }}
                      className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

       {showAddTestModal && <AddTestForm />}
      
      {showLevelSelector && selectedTest && (
        <LevelSelector 
          test={selectedTest}
          onLevelSelect={handleLevelSelect}
          onClose={() => {
            setShowLevelSelector(false);
            setSelectedTest(null);
          }}
        />
      )}
      
      {showQuestionsViewer && selectedTest && selectedViewLevel && (
        <QuestionsViewer
          test={selectedTest}
          level={selectedViewLevel}
          onEditQuestion={handleEditQuestion}
          onClose={() => {
            setShowQuestionsViewer(false);
            setSelectedViewLevel(null);
          }}
        />
      )}
      
      {showAddQuestionsModal && selectedTest && selectedLevel && (
        <AddQuestionsForm />
      )}
      
      {showEditQuestionModal && selectedQuestion && (
        <EditQuestionForm />
      )}
      
      {showSettingsModal && selectedTest && <TestSettingsForm />}
      
      {showDeleteConfirmation && testToDelete && (
        <DeleteConfirmation 
          test={testToDelete} 
          onCancel={() => {
            setShowDeleteConfirmation(false);
            setTestToDelete(null);
          }}
          onConfirm={handleDeleteTest}
        />
      )}

       <footer className="bg-white border-t border-gray-200 mt-8 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-gray-500 text-sm">
        </div>
      </footer>
      
      <style jsx="true">{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -20px); }
          10% { opacity: 1; transform: translate(-50%, 0); }
          90% { opacity: 1; transform: translate(-50%, 0); }
          100% { opacity: 0; transform: translate(-50%, -20px); }
        }
        
        .animate-fade-in-out {
          animation: fadeInOut 3s ease-in-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default TestsManagement;